import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import NotificationDetailSheet from './NotificationDetailSheet';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { timeAgoKey, translateNotificationTitle, translateNotificationBody } from '@/lib/dateUtils';
import { resolveNotificationRoute } from '@/lib/notificationRoutes';

export interface NotificationItem {
  id: string;
  user_id: string | null;
  project_id: string | null;
  project_name: string | null;
  notification_type: string;
  title: string;
  body: string;
  target_type: string | null;
  target_id: string | null;
  target_role: string | null;
  link_path: string | null;
  related_table?: string | null;
  is_read: boolean;
  created_at: string;
  // UI-enriched fields
  icon: string;
  color: string;
  project?: string;
  images?: string[];
  files?: { name: string; size: string; type: string }[];
}

const typeConfig: Record<string, { icon: string; color: string }> = {
  progress:   { icon: 'ri-checkbox-circle-line',  color: '#059669' },
  issue:      { icon: 'ri-error-warning-line',     color: '#EF4444' },
  inspection: { icon: 'ri-shield-check-line',      color: '#059669' },
  approval:   { icon: 'ri-file-list-3-line',       color: '#E8621A' },
  question:   { icon: 'ri-question-answer-line',   color: '#E8621A' },
  task:       { icon: 'ri-task-line',              color: '#F59E0B' },
  system:     { icon: 'ri-information-line',       color: '#9CA3AF' },
  payment:    { icon: 'ri-bank-card-line',         color: '#059669' },
  update:     { icon: 'ri-megaphone-line',         color: '#E8621A' },
  photo:      { icon: 'ri-image-line',             color: '#0891B2' },
  file:       { icon: 'ri-folder-upload-line',      color: '#7C3AED' },
  schedule:   { icon: 'ri-calendar-line',          color: '#F59E0B' },
  instruction:{ icon: 'ri-file-list-3-line',       color: '#E8621A' },
  checklist:  { icon: 'ri-list-check-2',           color: '#374151' },
};

function getTypeConfig(type: string) {
  return typeConfig[type?.toLowerCase()] || { icon: 'ri-notification-3-line', color: '#9CA3AF' };
}

export default function ManagementNotifications() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, profile } = useAuth();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [selectedNotif, setSelectedNotif] = useState<NotificationItem | null>(null);

  const filterTypes = [
    { key: 'All', label: t('notifications.filterAll') },
    { key: 'Progress', label: t('notifications.filterProgress') },
    { key: 'Issue', label: t('notifications.filterIssue') },
    { key: 'Approval', label: t('notifications.filterApproval') },
    { key: 'Question', label: t('notifications.filterQuestion') },
    { key: 'Task', label: t('notifications.filterTask') },
    { key: 'Payment', label: t('notifications.filterPayment') },
    { key: 'Update', label: t('notifications.filterUpdate') },
    { key: 'Photo', label: t('notifications.filterPhoto') },
    { key: 'File', label: t('notifications.filterFile') },
  ];

  function timeAgo(iso: string): string {
    const { key, params } = timeAgoKey(iso);
    return t(key, params);
  }

  useEffect(() => {
    if (user) loadNotifications();
  }, [user]);

  async function loadNotifications() {
    setLoading(true);
    const role = profile?.user_type ?? 'management';

    const { data: notifs } = await supabase
      .from('jphph_notifications')
      .select('*')
      .or(`user_id.eq.${user!.id},user_id.is.null`)
      .eq('is_visible', true)
      .order('created_at', { ascending: false })
      .limit(100);

    if (!notifs) { setLoading(false); return; }

    // Get read status for current user
    const { data: reads } = await supabase
      .from('jphph_notification_reads')
      .select('notification_id')
      .eq('user_id', user!.id);

    const readSet = new Set((reads || []).map((r) => r.notification_id));

    // Get project names for any missing project_name values
    const projectIds = [...new Set(notifs.map((n) => n.project_id).filter(Boolean))];
    let projectNames: Record<string, string> = {};
    if (projectIds.length > 0) {
      const { data: projects } = await supabase
        .from('jphph_projects')
        .select('id, project_name')
        .in('id', projectIds);
      (projects || []).forEach((p) => { projectNames[p.id] = p.project_name; });
    }

    const enriched: NotificationItem[] = notifs
      .filter((n) => n.target_role === role || !n.target_role || n.user_id === user!.id)
      .map((n) => {
        const cfg = getTypeConfig(n.notification_type);
        return {
          ...n,
          icon: cfg.icon,
          color: cfg.color,
          is_read: readSet.has(n.id) || n.is_read,
          project: n.project_name ?? (n.project_id ? projectNames[n.project_id] : undefined),
        };
      });

    setItems(enriched);
    setLoading(false);
  }

  async function markRead(notifId: string) {
    if (!user) return;
    await supabase.from('jphph_notification_reads').upsert(
      { notification_id: notifId, user_id: user.id },
      { onConflict: 'notification_id,user_id' }
    );
    await supabase.from('jphph_notifications').update({ is_read: true, read_at: new Date().toISOString() }).eq('id', notifId);
    setItems((prev) => prev.map((n) => n.id === notifId ? { ...n, is_read: true } : n));
  }

  async function markAllRead() {
    if (!user) return;
    const unread = items.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    await supabase.from('jphph_notification_reads').upsert(
      unread.map((n) => ({ notification_id: n.id, user_id: user.id })),
      { onConflict: 'notification_id,user_id' }
    );
    await supabase.from('jphph_notifications').update({ is_read: true, read_at: new Date().toISOString() }).in('id', unread.map((n) => n.id));
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }

  const handleOpen = async (n: NotificationItem) => {
    setSelectedNotif(n);
    if (!n.is_read) await markRead(n.id);
  };

  const handleNavigate = (n: NotificationItem) => {
    const route = resolveNotificationRoute(n, 'management');
    if (route) navigate(route);
  };

  const filtered = items.filter((n) => {
    if (activeFilter === 'All') return true;
    return n.notification_type?.toLowerCase() === activeFilter.toLowerCase();
  });


  const unreadCount = items.filter((n) => !n.is_read).length;

  return (
    <MgmtLayout title={t('notifications.title')}>
      <div className="px-4 pt-4 pb-4 w-full max-w-full overflow-x-hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-[15px] font-bold text-[#1A1A1A] whitespace-normal sm:whitespace-nowrap">{t('notifications.allNotifications')}</h2>
            {unreadCount > 0 && (
              <span className="bg-[#E8621A] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0">{unreadCount}</span>
            )}
          </div>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-[#E8621A] text-sm font-medium cursor-pointer whitespace-nowrap flex-shrink-0 ml-2">
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4 scrollbar-hide">
          {filterTypes.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                activeFilter === f.key ? 'bg-[#E8621A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-[#E8621A]">
                <i className="ri-loader-4-line text-3xl animate-spin"></i>
              </div>
              <p className="text-[#9CA3AF] text-sm">{t('notifications.loading')}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((n) => (
              <button
                key={n.id}
                onClick={() => { handleOpen(n); handleNavigate(n); }}
                className={`w-full text-left rounded-2xl border p-4 flex items-start gap-3 cursor-pointer transition-colors ${
                  !n.is_read ? 'bg-white border-gray-100' : 'bg-[#F9FAFB] border-transparent'
                }`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${n.color}15` }}>
                  <i className={`${n.icon} text-lg`} style={{ color: n.color }}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm leading-snug ${!n.is_read ? 'font-semibold text-[#1A1A1A]' : 'font-medium text-[#374151]'}`}>
                      {translateNotificationTitle(n.notification_type, n.title, t)}
                    </p>
                    {!n.is_read && <div className="w-2 h-2 rounded-full bg-[#E8621A] flex-shrink-0 mt-1"></div>}
                  </div>
                  <p className="text-[#9CA3AF] text-xs mt-0.5 leading-snug line-clamp-2">{translateNotificationBody(n.notification_type, n.body, t, i18n.language)}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-[#9CA3AF] text-xs">{timeAgo(n.created_at)}</span>
                    {n.project && (
                      <>
                        <span className="text-[#D1D5DB] text-xs">·</span>
                        <span className="text-[#9CA3AF] text-xs truncate">{n.project}</span>
                      </>
                    )}
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: `${n.color}15`, color: n.color }}>
                      {n.notification_type}
                    </span>
                  </div>
                </div>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-[#D1D5DB]">
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </div>
              </button>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
              <i className="ri-notification-off-line text-5xl"></i>
            </div>
            <div className="text-[#374151] font-semibold mb-1">{t('notifications.noNotifications')}</div>
            <div className="text-[#9CA3AF] text-sm">{t('notifications.allCaughtUp')}</div>
          </div>
        )}
      </div>

      {selectedNotif && (
        <NotificationDetailSheet
          notification={selectedNotif}
          onClose={() => setSelectedNotif(null)}
        />
      )}
    </MgmtLayout>
  );
}
